/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from "react";
import { loadGoogleMapsScript, parsePlace, type ParsedAddress } from "@/lib/googleMaps";

// Lightweight ref-attach hook for Google Places address autocomplete.
//
// Why a hook instead of just using <AddressAutocomplete>:
// the existing component wraps shadcn <Input>, which carries its own
// styling defaults. The new concierge surfaces (OrderConcierge,
// ConciergeWizard) use custom butter-themed inputs and need to keep
// their own look. This hook hands back a ref the caller attaches to
// any input, autocomplete logic stays decoupled from styling.
//
// Usage:
//   const addrRef = useAddressAutocomplete((parsed) => {
//     setBrief((b) => ({ ...b, address: parsed.formatted }));
//   });
//   return <input ref={addrRef} value={brief.address} ... />;
//
// If Google Maps fails to load (network, blocked, key issue), the
// input still works as a plain text field, no autocomplete, but no
// breakage either.

export function useAddressAutocomplete(
  onSelect: (parsed: ParsedAddress) => void,
  options: { country?: string } = {},
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onSelectRef = useRef(onSelect);
  const country = options.country ?? "au";

  // Keep the latest callback in a ref so we don't re-bind the
  // listener every render (which would also re-create the autocomplete
  // instance and cause Google's dropdown to flicker).
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let cancelled = false;
    let autocomplete: any;
    let listener: any;

    loadGoogleMapsScript()
      .then(() => {
        if (cancelled || !inputRef.current || !window.google?.maps?.places) return;
        autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country },
          fields: ["address_components", "formatted_address"],
          types: ["address"],
        });
        listener = autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const parsed = parsePlace(place);
          if (parsed) onSelectRef.current(parsed);
        });
      })
      .catch(() => {
        // Silent fall-through to plain input. Logging once is fine; we
        // don't want a console.error per render.
        console.warn("Google Maps failed to load, address field is plain-text only.");
      });

    return () => {
      cancelled = true;
      if (listener?.remove) listener.remove();
    };
  }, [country]);

  return inputRef;
}
